import React, { useMemo } from "react";
import { Row, Col, Divider } from "antd";
import { ITripDetails } from "../../ServiceUsers/Interfaces/ITripDetails";
import styles from "./BookingSummary.module.scss";
import textStyles from "../../Shared/Styles/TextStyles.module.scss";
import tripCardStyles from "../../Shared/Cards/TripCards/TripCards.module.scss";
import dividerStyles from "../../Shared/Styles/DividerStyles.module.scss";
import { getGrandTotalCost } from "../../../helpers/tripHelper";

type Props = {
  trip: ITripDetails[];
};

function GrandTotal({ trip }: Props) {
  // all discounts applied to an itinerary
  const discountInfo = useMemo(function() {
    return {
      isWandaPlusTrip: trip[0]?.wandaSubscriptionPlan === "Plus",
      hasPromoCode: !!trip[0]?.promoCodeDetail?.promoCode,
      wandaPlusDiscountPercent: trip[0]?.wandaPlanPercentage,
      promoCodeDiscountPercent: trip[0]?.promoCodeDetail?.discountValue,
      isPromoCodePercent:
        trip[0]?.promoCodeDetail?.discountType === "Percentage",
      promoCodeName: trip[0]?.promoCodeDetail?.promoCode,
      totalWandaPlusDiscount: trip
        .reduce(
          (acc: number, leg: ITripDetails) => leg.wandaPlusDiscountCost + acc,
          0
        )
        .toFixed(2),
      totalPromoCodeDiscount: trip
        .reduce(
          (acc: number, leg: ITripDetails) =>
            leg.promoCodeDetail?.discountedAmount + acc,
          0
        )
        .toFixed(2),
    };
  }, []);

  return (
    <Row className={styles.grandTotalSection}>
      <Col span={24} className={textStyles.normalText20}>
        <Row gutter={[0, 16]}>
          Total <Col span={24} />
        </Row>
      </Col>

      <Col span={24}>
        <Row className={tripCardStyles.costDetailsAlign} gutter={[0, 4]}>
          <Col span={12} className={textStyles.boldText14}>
            Trips ({trip.length})
          </Col>
          <Col span={12} className={textStyles.normalText14}>
            ${getGrandTotalCost(trip).tripCostWithoutDiscount}
          </Col>
          <Col span={24} />
        </Row>

        <Row
          className={`${textStyles.fadedNormalText14} ${tripCardStyles.overallCostAlign}`}
          gutter={[0, 4]}
        >
          {discountInfo.isWandaPlusTrip && (
            <>
              <Col span={12}>Wanda Plus Discount</Col>
              <Col span={12} className={textStyles.greenBoldText14}>
                {discountInfo.wandaPlusDiscountPercent}%{"\u00A0\u00A0"}-$
                {discountInfo.totalWandaPlusDiscount}
              </Col>
            </>
          )}

          {discountInfo.hasPromoCode && (
            <>
              <Col span={12}>Promo Code</Col>
              <Col span={12} className={textStyles.greenBoldText14}>
                {discountInfo.isPromoCodePercent &&
                  `${discountInfo.promoCodeDiscountPercent}%`}
                {"\u00A0\u00A0"}-${discountInfo.totalPromoCodeDiscount}
              </Col>
              <Col span={24}>{discountInfo.promoCodeName}</Col>
            </>
          )}

          <Divider className={dividerStyles.solidFadedDivider} />
        </Row>

        <Row
          className={`${textStyles.normalText16} ${tripCardStyles.overallCostAlign}`}
          gutter={[0, 4]}
        >
          <Col span={12}>Total cost</Col>
          <Col span={12}>${getGrandTotalCost(trip).tripCostWithDiscount}</Col>
          <Col span={24} className={textStyles.normalText14}>
            You'll only be charged after each individual trip is complete.
          </Col>
        </Row>
      </Col>
    </Row>
  );
}

export default GrandTotal;
